import React, { useEffect, useState, useRef } from "react";
import { string, bool, shape, object } from "prop-types";
import cx from "classnames";
import * as R from "ramda";

import initialState from "./initialState.js";

import { scrollToId } from "../../common/scrollHelpers.js";
import { initFacets } from "../../common/dynamicSearch.js";
import fetchSearchResults from "../../common/fetchSearchResults.js";

import Pagination from "../Pagination/Pagination.jsx";
import ExpertProfileCard from "../ExpertProfileCard/ExpertProfileCard.jsx";
import FilterBar from "../FilterBar/FilterBar.jsx";
import ResultsInfo from "../ResultsInfo/ResultsInfo.jsx";
import SimpleSearchBox from "../SimpleSearchBox/SimpleSearchBox.jsx";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner.jsx";

import {
  createQueryString,
  parseQueryString,
} from "../../common/dynamicSearch.js";

const propTypes = {
  id: string.isRequired,
  title: string,
  enableQueryString: bool,
  pagination: bool,
  search: bool,
  hideMobileSummary: bool,
  url: string.isRequired,
  type: string,
  defaultFacet: object,
  dictionary: object.isRequired,
  query: shape({
    listingId: string,
    showSummaries: bool,
    showDates: bool,
    showContentTypes: bool,
    showImages: string,
    loadAllPages: bool,
    pageId: string,
  }).isRequired,
};

const DynamicLeadershipListingApp = (props) => {
  const {
    id,
    title,
    enableQueryString,
    pagination,
    search,
    url,
    type,
    defaultFacet,
    dictionary,
    query,
  } = props;

  const generalParams = {
    ...initialState.query,
    ...query,
  };

  const [showOverlay, setShowOverlay] = useState(false);

  const [selectedFacets, setSelectedFacets] = useState(() => {
    const keyword = parseQueryString(window.location.search).q;
    const requestedFacets = initFacets(window.location.search);

    return !R.isEmpty(requestedFacets)
      ? requestedFacets
      : !keyword
        ? defaultFacet
        : {};
  });
  const [serverData, setServerData] = useState(null);

  //initialize the query to that of the query string if it exists
  const [currentParams, setCurrentParams] = useState(() => {
    const keyword = parseQueryString(window.location.search).q;

    return {
      ...generalParams,
      ...(keyword ? { q: keyword } : {}),
      ...selectedFacets,
    };
  });

  const [resultMessage, setResultMessage] = useState(null);

  const resultsFocusRef = useRef();
  const showFocusRef = useRef({ showFocus: false });

  const ignoreFetch = useRef({ ignore: false });

  const handleQueryChange = (term) => {
    if (term !== "") {
      ignoreFetch.current.ignore = true;
      setSelectedFacets({});
      ignoreFetch.current.ignore = false;
    }
    setCurrentParams((prevState) => {
      if (serverData.facets && term !== "") {
        serverData.facets.forEach((facet) => {
          delete prevState[facet.id];
        });
      }
      return { ...prevState, q: term, page: 1 };
    });
  };

  const handleFacetChange = (facetGroupId, facetValue) => {
    if (serverData.facets) {
      serverData.facets.forEach((facet) => {
        delete currentParams[facet.id];
      });
    }

    const newParams = { ...currentParams, ...selectedFacets, page: 1 };

    facetValue === ""
      ? delete newParams[facetGroupId]
      : (newParams[facetGroupId] = [facetValue]);

    setCurrentParams(newParams);
  };

  // Not used with single select
  const handleClearFacetGroup = (facetGroupValue) => {
    // setSelectedFacets((prevState) => {
    //   return clearFacetGroup(facetGroupValue, prevState);
    // });
  };

  const handlePaginationChange = (page) => {
    setCurrentParams((prevState) => {
      return { ...prevState, page: page };
    });
    // move the focus back to the top of the results on page change
    scrollToId(`${id}-results`);
    showFocusRef.current.showFocus = true;
  };

  useEffect(() => {
    if (dictionary.facetMessages) {
      setResultMessage(
        dictionary.facetMessages.reduce((activeMessage, facetMessage) => {
          if (selectedFacets[facetMessage.id]) {
            const messageObject = facetMessage.messages.find(
              (message) => message.id === selectedFacets[facetMessage.id][0],
            );
            return messageObject;
          } else {
            return activeMessage;
          }
        }, {}),
      );
    }
  }, [dictionary.facetMessages, selectedFacets]);

  //update the url with the latest query parameters
  useEffect(() => {
    if (enableQueryString) {
      const queryString = createQueryString(currentParams, selectedFacets);

      if (queryString.length > 0) {
        window.history.replaceState(
          "",
          "",
          window.location.origin +
          window.location.pathname +
          "?" +
          createQueryString(currentParams, selectedFacets) +
          window.location.hash,
        );
      } else {
        window.history.replaceState(
          "",
          "",
          window.location.origin +
          window.location.pathname +
          window.location.hash,
        );
      }
    }
  }, [currentParams, enableQueryString, selectedFacets]);

  useEffect(() => {
    if (ignoreFetch.current.ignore) {
      ignoreFetch.current.ignore = false;
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    setShowOverlay(true);

    fetchSearchResults(
      url,
      {
        ...currentParams,
      },
      type,
      signal,
    )
      .then(function (data) {
        if (data) {
          setServerData(data);

          const origFacets = {};
          if (data.facets) {
            data.facets.forEach((facet) => {
              const selectedValues = facet.values.reduce((filtered, value) => {
                if (value.selected) {
                  filtered.push(value.id);
                }
                return filtered;
              }, []);

              if (selectedValues.length > 0) {
                origFacets[facet.id] = selectedValues;
              }
            });
          }

          setSelectedFacets(origFacets);
        }
      })
      .catch(function (e) {
        if (e.name === "AbortError") {
          return; // Continuation logic has already been skipped, so return normally
        }
        console.warn("request error: " + e.message);
      })
      .finally(function () {
        setShowOverlay(false);
      });
    // unmounts or data changes
    return () => {
      controller.abort();
    };
  }, [currentParams, type, url]);

  useEffect(() => {
    if (resultsFocusRef.current && showFocusRef.current.showFocus) {
      //move the focus back to the top of the results on page change
      resultsFocusRef.current.focus();
      showFocusRef.current.showFocus = false;
    }
  }, [serverData]);

  return (
    <div className="dynamic-leadership-listing" id={id}>
      {title && (
        <h2 className="dynamic-leadership-listing__heading">{title}</h2>
      )}
      {search && (
        <div className="dynamic-leadership-listing__search-box">
          <SimpleSearchBox
            searchTerm={currentParams.q}
            onTextChange={handleQueryChange}
            placeholder={dictionary.searchPlaceholder}
          />
        </div>
      )}
      {serverData && serverData.facets && serverData.facets.length > 0 && (
        <div className="dynamic-leadership-listing__filter-bar">
          <FilterBar
            filterTitle={dictionary.filterTitle}
            filterLabel={dictionary.filterLabel}
            onFacetChange={handleFacetChange}
            facets={serverData.facets}
            onClearFacetGroup={handleClearFacetGroup}
            selectedFacets={selectedFacets}
            singleSelect={true}
          />
        </div>
      )}
      {serverData && serverData.totalResults > 0 && (
        <div className="dynamic-leadership-listing__results-info">
          <ResultsInfo
            totalResults={serverData.totalResults}
            request={serverData.request}
            resultsInfoLabel={dictionary.resultsInfoLabel}
            query={currentParams.q}
            clearAllFacetsLabel={dictionary.clearAll}
          />
        </div>
      )}
      <div className="dynamic-leadership-listing__results" id={`${id}-results`}>
        {showOverlay && <LoadingSpinner />}
        {resultMessage && resultMessage.title && (
          <div
            className={cx("dynamic-leadership-listing__result-message", {
              "dynamic-leadership-listing__result-message--loading":
                showOverlay,
            })}
          >
            <div className="dynamic-leadership-listing__message-title">
              {resultMessage.title}
            </div>
            {resultMessage.description && (
              <div className="dynamic-leadership-listing__message-description">
                {resultMessage.description}
              </div>
            )}
          </div>
        )}
        {serverData && serverData.results && serverData.results.length < 1 && (
          <div
            className="dynamic-leadership-listing__no-results"
            aria-live="polite"
          >
            {dictionary.noResultsMessage || "No Results Found"}
          </div>
        )}
        {!showOverlay &&
          serverData &&
          serverData.results &&
          serverData.results.length > 0 && (
            <div aria-live="polite" className="u-visuallyhidden">
              New Results
            </div>
          )}
        {serverData && serverData.results && serverData.results.length > 0 && (
          <ul className="dynamic-leadership-listing__items">
            {serverData.results.map((result, i) => {
              return (
                <li
                  key={result.key || result.id || i}
                  className="dynamic-leadership-listing__item"
                  tabIndex="-1"
                  ref={i === 0 ? resultsFocusRef : null}
                >
                  <ExpertProfileCard {...result} />
                  <figure className="dynamic-leadership-listing__mobile-result-media">
                    <a href={result.cta.href} title={result.cta.title}>
                      <img
                        srcSet={result.image.sources}
                        alt={result.image.alt}
                      />
                    </a>
                  </figure>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {serverData &&
        pagination &&
        serverData.totalResults > serverData.request.perPage && (
          <div className="dynamic-leadership-listing__pagination">
            <Pagination
              totalResults={serverData.totalResults}
              //this is currently returned as a string from the server, so convert to number
              resultsPerPage={Number(serverData.request.perPage)}
              currentPage={Number(currentParams.page)}
              onPaginationChange={handlePaginationChange}
            />
          </div>
        )}
    </div>
  );
};

DynamicLeadershipListingApp.propTypes = propTypes;
export default DynamicLeadershipListingApp;
